import { getLocales } from "expo-localization";
import type { MultiFactorResolver } from "firebase/auth";
import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import { LanguageType, SUPPORTED_LANGUAGES } from "@libs/i18n";
import { ColorSchemeType } from "@/context/ThemeContext";
import { APPLICATION_FLOW_DEFAULT_STEPS } from "@/domain/applications";
import { DashboardInputsType } from "@/domain/dashboardCards";
import { PendingLanguageType } from "@/domain/languages";
import { GetShortCodeResponseType } from "@/domain/verifyApi";
import {
  getItem,
  removeItem,
  setItem,
  StorageKeyType,
  subscribe,
} from "@/utils/storage";

export const atomWithMMKV = <T>(key: StorageKeyType, initialValue: T) =>
  atomWithStorage<T>(
    key,
    initialValue,
    createJSONStorage<T>(() => ({
      getItem,
      setItem,
      removeItem,
      subscribe,
    })),
    { getOnInit: true },
  );

// Settings Atoms (Get default values from device locale)
const DEVICE_LOCALE = getLocales()[0];

const DEVICE_LANGUAGE = DEVICE_LOCALE?.languageCode as LanguageType;

export const localeAtom = atomWithMMKV<LanguageType>(
  "LANGUAGE_CODE",
  SUPPORTED_LANGUAGES.includes(DEVICE_LANGUAGE) ? DEVICE_LANGUAGE : "en",
);

/** Explicit choice awaiting patient sync, separate from the device-derived locale. */
export const pendingLanguageAtom = atomWithMMKV<PendingLanguageType | null>(
  "PENDING_LANGUAGE",
  null,
);

export const themeAtom = atomWithMMKV<ColorSchemeType>(
  "SELECTED_THEME",
  "light",
);

/**
 * Stores the email address of the user attempting to sign in with Firebase magic link.
 * When the magic link is received and opened, Firebase compares this stored email
 * with the link's email to verify the sign-in request came from the same device,
 * completing the authentication flow.
 */
export const emailForSigninAtom = atomWithMMKV<string | null>(
  "EMAIL_FOR_SIGN_IN_KEY",
  null,
);

/**
 * Stores whether the user has completed the registration process.
 * Used to determine if registration screens should be shown.
 */
export const hasCompletedRegistrationAtom = atomWithMMKV<boolean>(
  "HAS_COMPLETED_REGISTRATION",
  false,
);

/**
 * Stores the current step in the application flow.
 */
export const applicationCurrentStepAtom = atomWithMMKV<number>(
  "APPLICATION_CURRENT_STEP",
  1,
);

/**
 * Stores the total number of steps in the application flow.
 * Default is 12, increases to 13 when broker consent step is shown.
 */
export const applicationTotalStepsAtom = atomWithMMKV<number>(
  "APPLICATION_TOTAL_STEPS",
  APPLICATION_FLOW_DEFAULT_STEPS,
);

/**
 * Stores the last synced eligibility data as a serialized JSON string.
 * Persists across app refreshes to prevent redundant eligibility result syncs.
 */
export const lastSyncedEligibilityDataAtom = atomWithMMKV<string>(
  "LAST_SYNCED_ELIGIBILITY_DATA",
  "",
);

/**
 * Stores the short code data retrieved from the server while in the onboarding flow.
 */
export const onboardingShortCodeDataAtom =
  atomWithMMKV<GetShortCodeResponseType | null>(
    "ONBOARDING_SHORT_CODE_DATA",
    null,
  );

/**
 * Stores the list of memberIds for which the user has already completed onboarding.
 * Used to skip the onboarding flow for returning patients on the same device.
 */
export const completedOnboardingMemberIdsAtom = atomWithMMKV<string[]>(
  "COMPLETED_ONBOARDING_MEMBER_IDS",
  [],
);

/**
 * In-memory flag set synchronously before a manual logout is triggered.
 * Allows _layout.tsx to redirect to /auth instead of /onboarding when auth
 * state changes. Not persisted — resets on app restart.
 */
export const isManualLogoutAtom = atomWithMMKV<boolean>(
  "IS_MANUAL_LOGOUT",
  false,
);

/**
 * In-memory flag indicating the user was logged out due to session expiry.
 * Used to redirect to the session-expired screen instead of the onboarding screen.
 */
export const isSessionExpiredAtom = atomWithMMKV<boolean>(
  "SESSION_TIMEOUT_CONTROL",
  false,
);

/**
 * Stores whether the user needs to create a password after magic link registration.
 */
export const needsToCreatePasswordAtom = atomWithMMKV<boolean>(
  "NEEDS_TO_CREATE_PASSWORD",
  false,
);

/**
 * Stores the Firebase MultiFactorResolver in memory.
 * Required to complete sign-in after presenting an MFA challenge.
 *
 * Note: This atom intentionally uses `atom` instead of `atomWithMMKV` (unlike the rest
 * of the file). The `MultiFactorResolver` is a complex Firebase SDK object containing
 * methods (like `resolveSignIn`). Attempting to persist it via `atomWithMMKV` would
 * JSON-serialize it, stripping all its methods and rendering it unusable.
 */
export const mfaResolverAtom = atom<MultiFactorResolver | null>(null);

/**
 * Stores the OTP verification ID in memory during MFA enrollment or sign-in.
 */
export const mfaVerificationIdAtom = atomWithMMKV<string | null>(
  "MFA_VERIFICATION_ID",
  null,
);

/**
 * Stores stable ids for dismissible dashboard "Next steps" cards. Local-only —
 * there is no server state backing dismissal. Persisted so a dismissal survives
 * app restarts. Each card kind owns its id format so new underlying data creates
 * a new card instead of inheriting an earlier dismissal.
 */
export const dismissedDashboardCardIdsAtom = atomWithMMKV<string[]>(
  "DISMISSED_DASHBOARD_CARD_IDS",
  [],
);

// Replaced per successful batch to avoid combining separate submission events.
export const submissionConfirmationApplicationIdsAtom = atomWithMMKV<string[]>(
  "SUBMISSION_CONFIRMATION_APPLICATION_IDS",
  [],
);

/**
 * NON-PROD-only override for the dashboard's derived inputs, set by the dashboard
 * scenario panel (non-production environments only). When non-null,
 * `useDashboardCards` builds the whole dashboard from these synthetic inputs
 * instead of the live queries.
 *
 * Intentionally a plain `atom` (not `atomWithMMKV`): it must NOT persist, so a
 * reload always returns to real data and a stale scenario can never silently
 * stick.
 */
export const dashboardScenarioAtom = atom<DashboardInputsType | null>(null);
